import React, { useState, useEffect, useContext } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { crd, bdg, sp, type, icBox, secH, listRow } from "../../constants/styles";
import Ic from "../common/Ic";

const TYPE_META = {
  broken:     { icon: "🐛", label: "Broken",     color: "#ef4444" },
  suggestion: { icon: "💡", label: "Suggestion", color: "#f59e0b" },
  help:       { icon: "❓", label: "Need Help",  color: "#3b82f6" },
  review:     { icon: "⭐", label: "Review",     color: "#10b981" },
  bug:        { icon: "🐛", label: "Bug",        color: "#ef4444" },
  feedback:   { icon: "💬", label: "Feedback",   color: "#3b82f6" },
  test:       { icon: "✅", label: "Test Note",  color: "#10b981" },
};

const STATUS_META = {
  new:         { label: "New",         color: "#ef4444", bg: "#ef444420" },
  "in-progress":{ label: "In Progress", color: "#f59e0b", bg: "#f59e0b20" },
  resolved:    { label: "Resolved",    color: "#10b981", bg: "#10b98120" },
};

const FILTERS = ["all", "new", "in-progress", "resolved"];

export default function AdminFeedback() {
  const { h, go, flash } = useContext(Ctx);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = async (f) => {
    setLoading(true);
    try {
      const res = await api.getFeedback(f === "all" ? null : f);
      if (res.success) setItems(res.feedback || []);
    } catch { flash("Failed to load feedback", "error"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(filter); }, [filter]);

  const deleteItem = async (id) => {
    setDeleting(id);
    try {
      const res = await api.deleteFeedback(id);
      if (res.success) setItems(prev => prev.filter(i => i.id !== id));
      else flash("Failed to delete", "error");
    } catch { flash("Network error", "error"); }
    finally { setDeleting(null); }
  };

  const updateStatus = async (id, status) => {
    setUpdating(id);
    try {
      const res = await api.updateFeedbackStatus(id, status);
      if (res.success) setItems(prev => prev.map(i => i.id === id ? { ...i, status } : i));
      else flash("Failed to update", "error");
    } catch { flash("Network error", "error"); }
    finally { setUpdating(null); }
  };

  const counts = {
    all: items.length,
    new: items.filter(i => i.status === "new").length,
    "in-progress": items.filter(i => i.status === "in-progress").length,
    resolved: items.filter(i => i.status === "resolved").length,
  };

  const displayed = filter === "all" ? items : items.filter(i => i.status === filter);

  return (
    <div style={{ paddingTop: sp.lg }} className="animate-fadeIn">
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: sp.md, marginBottom: sp.xl }}>
        <button onClick={() => go("dash")} style={{ background: "none", border: "none", cursor: "pointer", padding: sp.xs }}>
          <Ic n="arrow-left" s={20} c={h.ac} />
        </button>
        <div>
          <h2 style={{ ...type.h1, color: h.tx, margin: 0 }}>Feedback Inbox</h2>
          <p style={{ ...type.sm, color: h.ts, marginTop: 2 }}>{items.length} total reports</p>
        </div>
      </div>

      {/* Summary Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: sp.sm, marginBottom: sp.xl }}>
        {[
          { key: "all",          label: "Total",       color: h.ac },
          { key: "new",          label: "New",         color: "#ef4444" },
          { key: "in-progress",  label: "In Progress", color: "#f59e0b" },
          { key: "resolved",     label: "Resolved",    color: "#10b981" },
        ].map(s => (
          <div key={s.key} onClick={() => setFilter(s.key)} style={{
            ...crd(h), padding: `${sp.md}px ${sp.sm}px`, textAlign: "center", cursor: "pointer",
            border: filter === s.key ? `2px solid ${s.color}` : `1px solid ${h.bd}`,
            marginBottom: 0,
          }}>
            <div style={{ ...type.h2, color: s.color }}>{loading ? "—" : counts[s.key]}</div>
            <div style={{ fontSize: 10, fontWeight: 600, color: h.ts, marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter Tabs */}
      <div style={{ display: "flex", gap: sp.sm, marginBottom: sp.lg, overflowX: "auto", paddingBottom: 2 }}>
        {FILTERS.map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: "6px 14px", borderRadius: 20, border: "none", cursor: "pointer", whiteSpace: "nowrap",
            background: filter === f ? h.ac : h.cd,
            color: filter === f ? "#fff" : h.ts,
            fontSize: 12, fontWeight: 600, transition: "all 0.15s",
            boxShadow: filter === f ? `0 2px 8px ${h.ac}40` : "none",
          }}>
            {f === "all" ? "All" : f === "in-progress" ? "In Progress" : f.charAt(0).toUpperCase() + f.slice(1)}
            {f !== "all" && counts[f] > 0 && (
              <span style={{
                marginLeft: 6, background: filter === f ? "rgba(255,255,255,0.3)" : STATUS_META[f]?.bg,
                color: filter === f ? "#fff" : STATUS_META[f]?.color,
                borderRadius: 10, padding: "1px 6px", fontSize: 10, fontWeight: 700,
              }}>{counts[f]}</span>
            )}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div style={{ textAlign: "center", paddingTop: 60 }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={h.ac} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "spin 1s linear infinite" }}>
            <path d="M21 12a9 9 0 11-6.219-8.56" />
          </svg>
        </div>
      ) : displayed.length === 0 ? (
        <div style={{ ...crd(h), textAlign: "center", padding: "40px 24px", color: h.ts }}>
          <div style={{ fontSize: 36, marginBottom: sp.md }}>📭</div>
          <div style={{ ...type.sm, fontWeight: 600 }}>No reports here</div>
          <div style={{ ...type.xs, marginTop: sp.xs }}>
            {filter === "all" ? "No feedback submitted yet" : `No ${filter} reports`}
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: sp.sm }}>
          {displayed.map(item => {
            const tm = TYPE_META[item.type] || TYPE_META.feedback;
            const sm = STATUS_META[item.status] || STATUS_META.new;
            return (
              <div key={item.id} style={{ ...crd(h), marginBottom: 0 }}>
                {/* Top row */}
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: sp.sm }}>
                  <div style={{ display: "flex", alignItems: "center", gap: sp.sm, flex: 1 }}>
                    <span style={{ fontSize: 20 }}>{tm.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: sp.xs, flexWrap: "wrap" }}>
                        <span style={bdg(`${tm.color}18`, tm.color)}>{tm.label}</span>
                        <span style={bdg(sm.bg, sm.color)}>{sm.label}</span>
                        {item.role && <span style={bdg(`${h.ac}12`, h.ac)}>{item.role}</span>}
                      </div>
                      <div style={{ ...type.xs, color: h.ts, marginTop: 3 }}>
                        📍 <span style={{ fontWeight: 600, color: h.tx }}>{item.page || "Unknown Screen"}</span>
                        {" · "}{item.created_at ? new Date(item.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                      </div>
                    </div>
                  </div>
                  {/* Delete button */}
                  <button
                    onClick={() => deleteItem(item.id)}
                    disabled={deleting === item.id}
                    title="Delete report"
                    style={{
                      background: "none", border: "none", cursor: "pointer",
                      padding: "4px 6px", borderRadius: 8, color: "#ef444488",
                      fontSize: 16, flexShrink: 0, transition: "color 0.15s",
                    }}
                  >
                    {deleting === item.id ? "..." : "🗑️"}
                  </button>
                </div>

                {/* Message */}
                <div style={{ ...type.body, color: h.tx, lineHeight: 1.5, padding: `${sp.sm}px ${sp.md}px`, background: `${h.ac}07`, borderRadius: 10, marginBottom: sp.md }}>
                  "{item.note}"
                </div>

                {/* Actions */}
                {item.status !== "resolved" && (
                  <div style={{ display: "flex", gap: sp.sm }}>
                    {item.status === "new" && (
                      <button
                        onClick={() => updateStatus(item.id, "in-progress")}
                        disabled={updating === item.id}
                        style={{
                          flex: 1, padding: "8px", borderRadius: 10, border: `1px solid #f59e0b`,
                          background: "#f59e0b14", color: "#f59e0b", fontSize: 12, fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        {updating === item.id ? "..." : "▶ In Progress"}
                      </button>
                    )}
                    <button
                      onClick={() => updateStatus(item.id, "resolved")}
                      disabled={updating === item.id}
                      style={{
                        flex: 1, padding: "8px", borderRadius: 10, border: `1px solid #10b981`,
                        background: "#10b98114", color: "#10b981", fontSize: 12, fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      {updating === item.id ? "..." : "✓ Resolve"}
                    </button>
                  </div>
                )}
                {item.status === "resolved" && (
                  <button
                    onClick={() => updateStatus(item.id, "new")}
                    disabled={updating === item.id}
                    style={{
                      width: "100%", padding: "7px", borderRadius: 10, border: `1px solid ${h.bd}`,
                      background: "transparent", color: h.ts, fontSize: 12, fontWeight: 500,
                      cursor: "pointer",
                    }}
                  >
                    ↩ Reopen
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div style={{ height: sp.xxxl }} />
    </div>
  );
}
